// Which customers may be offered as the parent of the customer being edited.
//
// This lives in its own module rather than inside EditCustomerForm so it can be
// unit-tested without mounting the connected component — the walk below is
// exactly the kind of logic that silently degrades to a no-op if the shape of
// its input changes, and a no-op here is invisible in the UI (M7-538).

// Ids of everything beneath `rootId`, walked iteratively off parent_id so an
// already-corrupt tree can't spin forever.
//
// `customers` is the FLAT list from `resources/view` (ResourceBlueprint), where
// `id` is serialized as a string and `parent_id` is the raw column — a string,
// empty for a root. Both sides of every comparison are String()-normalized
// because the edited record's parent_id arrives from a different payload and is
// not reliably the same type.
export function descendantIdsOf(customers, rootId) {
    const childrenByParent = new Map();
    customers.forEach(candidate => {
        const key = String(candidate.parent_id == null ? '' : candidate.parent_id);
        if (!childrenByParent.has(key)) {
            childrenByParent.set(key, []);
        }
        childrenByParent.get(key).push(candidate);
    });

    const descendants = new Set();
    const queue = [String(rootId)];
    while (queue.length > 0) {
        const parentId = queue.shift();
        (childrenByParent.get(parentId) || []).forEach(child => {
            const childId = String(child.id);
            if (descendants.has(childId)) {
                return;
            }
            descendants.add(childId);
            queue.push(childId);
        });
    }
    return descendants;
}

export function getTwoLevelCustomerList(customers, customer) {
    // A customer may not become a child of itself or of one of its own
    // sub-customers. That cycle detaches the whole subtree from every root, so it
    // then renders under no filter at all — which is how a customer lost an entire
    // category and the 19 customers under it (M7-538). The colon-depth heuristic
    // below is not a structural guard; it only caps nesting.
    //
    // This is defense in depth, NOT the authoritative check. `resources/view`
    // defaults to the "active" filter, so hidden customers are absent from
    // `customers` — a hidden intermediate node breaks the chain, and a deeper
    // active descendant beneath it stays selectable here. The server-side
    // validation (Customer#parent_must_not_be_self_or_descendant) walks the real
    // tree including hidden rows and rejects that case with a 400, so the failure
    // mode is "the dropdown offered something the server refuses", not corruption.
    const selfId = customer && customer.id != null ? String(customer.id) : null;
    const excludedIds = selfId ? descendantIdsOf(customers, selfId) : new Set();
    if (selfId) {
        excludedIds.add(selfId);
    }

    let updatedList = [];
    customers.forEach(candidate => {
        if (excludedIds.has(String(candidate.id))) {
            return;
        }
        if (candidate.name.split(':').length < 4) {
            updatedList.push(candidate);
        }
    });
    if (
        customer.parent_name !== '' &&
        customer.parent_hidden &&
        !excludedIds.has(String(customer.parent_id))
    ) {
        updatedList.push({ name: customer.parent_name, id: customer.parent_id });
    }
    return updatedList;
}
